import React, { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { m } from 'framer-motion';
import Header from './Header';
import Footer from './Footer';
import CookieBanner from './CookieBanner';
import NotFoundPage from './NotFoundPage';
import { SITE_ORIGIN, internalUrl, localAreaUrl } from './siteConfig';
import { getAreaBySlug } from './localAreasData';
import { SOUTH_WEST_HUB_SLUGS } from './servicePillarContent';
import { getLocalAreaDeepContent } from './localAreaDeepContent';
import { getAuthorityLocalBusinessNode } from './localAreaAuthoritySchema';
import LocalAreaTailSection from './LocalAreaTailSection';
import LocalAreaSpecialisms from './LocalAreaSpecialisms';
import LocalAreaGrowthSections from './LocalAreaGrowthSections';
import FreeSeoAuditCta from './FreeSeoAuditCta';
import {
  DEFAULT_META_DESCRIPTION,
  DEFAULT_PAGE_TITLE,
  getMetaDescriptionEl,
  setCanonical
} from './seoDefaults';
import {
  constructionTradeKnowsAbout,
  isTopLandingTownSlug,
  localAreaAggregateRating,
  localAreaClickyTitle,
  localAreaCtaMetaDescription,
  webcodecraftProfessionalServiceSchemaId
} from './localAreaPageSeo';
import {
  ORGANIZATION_SCHEMA_ID,
  organizationSchemaNode,
  webSiteSchemaNode,
  WEBSITE_SCHEMA_ID
} from './organizationSchema';

const NEARBY_LOCATIONS = {
  plymouth: [
    { slug: 'exeter', name: 'Exeter' },
    { slug: 'torbay', name: 'Torbay' },
    { slug: 'torquay', name: 'Torquay' },
    { slug: 'paignton', name: 'Paignton' },
    { slug: 'newton-abbot', name: 'Newton Abbot' },
    { slug: 'tavistock', name: 'Tavistock' },
    { slug: 'ivybridge', name: 'Ivybridge' },
    { slug: 'totnes', name: 'Totnes' },
    { slug: 'dartmouth', name: 'Dartmouth' },
    { slug: 'kingsbridge', name: 'Kingsbridge' },
    { slug: 'salcombe', name: 'Salcombe' },
    { slug: 'okehampton', name: 'Okehampton' },
    { slug: 'exmouth', name: 'Exmouth' },
    { slug: 'teignmouth', name: 'Teignmouth' },
    { slug: 'dawlish', name: 'Dawlish' }
  ],
  truro: [
    { slug: 'falmouth', name: 'Falmouth' },
    { slug: 'penzance', name: 'Penzance' },
    { slug: 'newquay', name: 'Newquay' },
    { slug: 'st-austell', name: 'St Austell' },
    { slug: 'camborne', name: 'Camborne' },
    { slug: 'redruth', name: 'Redruth' },
    { slug: 'bodmin', name: 'Bodmin' },
    { slug: 'helston', name: 'Helston' },
    { slug: 'st-ives', name: 'St Ives' },
    { slug: 'hayle', name: 'Hayle' },
    { slug: 'padstow', name: 'Padstow' },
    { slug: 'wadebridge', name: 'Wadebridge' },
    { slug: 'lostwithiel', name: 'Lostwithiel' },
    { slug: 'looe', name: 'Looe' },
    { slug: 'perranporth', name: 'Perranporth' }
  ]
};

function variantIndex(slug) {
  let n = 0;
  for (let i = 0; i < slug.length; i += 1) n += slug.charCodeAt(i);
  return n % 3;
}

function leadParagraph(area) {
  const v = variantIndex(area.slug);
  const blocks = [
    `Businesses in ${area.name} often need a clear website, dependable hosting, and HTTPS without a large upfront bill. WebCodeCraft is based in the South West and delivers custom web builds with monthly hosting and care, SSL included, and technical SEO foundations suited to how people search locally in ${area.county}.`,
    `If you trade or run a professional practice in ${area.name}, your site should explain services quickly, load well on mobile, and stay secure. We design and build bespoke pages, then keep patches, backups, and agreed support on a predictable monthly plan—useful when you want custom work without a traditional large upfront invoice.`,
    `From ${area.name} to the wider ${area.county} market, we help SMEs launch and iterate: semantic page structure, UK hosting options, SSL, and honest scope. You choose a tier on our services overview; larger changes stay quoted separately so monthly fees stay understandable.`
  ];
  return blocks[v];
}

function secondaryParagraph(area) {
  const v = variantIndex(`${area.slug}-b`);
  const blocks = [
    `Local searches such as web design near ${area.name} or website help in ${area.county} reward pages that state clearly what you do, where you operate, and how to enquire. We bake that clarity into layout and metadata while keeping performance and accessibility in mind.`,
    `Every published tier includes an SSL certificate, backup-minded hosting workflows, and security-oriented maintenance as described on the site. Optional SEO and security retainers can deepen monitoring where it makes sense for your sector.`,
    `We work remotely with ${area.county} clients as well as the rest of the UK. Use the contact section to describe your goals; we reply with next steps and realistic timelines.`
  ];
  return blocks[v];
}

function LocalAreaPage() {
  const { slug } = useParams();
  const area = getAreaBySlug(slug);
  const deep = area ? getLocalAreaDeepContent(area.slug) : null;
  const popularSouthWest = useMemo(() => {
    if (!area) return [];
    const picks = [];
    for (const h of SOUTH_WEST_HUB_SLUGS) {
      if (picks.length >= 3) break;
      if (h.slug === area.slug) continue;
      if (getAreaBySlug(h.slug)) picks.push({ slug: h.slug, name: h.label });
    }
    return picks;
  }, [area]);

  const canonical = area ? localAreaUrl(area.slug) : `${SITE_ORIGIN}/`;
  const schemaId = area ? `webcodecraft-local-${area.slug}` : '';

  const jsonLd = useMemo(() => {
    if (!area) return null;
    const svcId = webcodecraftProfessionalServiceSchemaId();
    const pageName = localAreaClickyTitle(area);
    const pageDesc = localAreaCtaMetaDescription(area);
    const served = {
      '@type': 'Place',
      name: area.name,
      containedInPlace: {
        '@type': 'AdministrativeArea',
        name: area.county,
        containedInPlace: { '@type': 'Country', name: 'United Kingdom' }
      }
    };
    if (deep?.wikidataSameAs) {
      served.sameAs = deep.wikidataSameAs;
    }
    const specialtyExtras = isTopLandingTownSlug(area.slug)
      ? { knowsAbout: constructionTradeKnowsAbout() }
      : {};

    const graph = [
      organizationSchemaNode(),
      webSiteSchemaNode(),
      {
        '@type': 'WebPage',
        '@id': `${canonical}#webpage`,
        url: canonical,
        name: pageName,
        description: pageDesc,
        isPartOf: { '@id': WEBSITE_SCHEMA_ID },
        inLanguage: 'en-GB',
        ...(isTopLandingTownSlug(area.slug) ? { knowsAbout: constructionTradeKnowsAbout() } : {})
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'WebCodeCraft', item: SITE_ORIGIN },
          { '@type': 'ListItem', position: 2, name: 'Areas we serve', item: `${SITE_ORIGIN}/locations` },
          { '@type': 'ListItem', position: 3, name: area.name, item: canonical }
        ]
      },
      {
        '@type': 'ProfessionalService',
        '@id': svcId,
        name: 'WebCodeCraft',
        url: SITE_ORIGIN,
        parentOrganization: { '@id': ORGANIZATION_SCHEMA_ID },
        areaServed: served
      }
    ];
    const lbExtras = {
      aggregateRating: localAreaAggregateRating(`${canonical}#aggregateRating`)
    };
    const authorityLb = getAuthorityLocalBusinessNode(area.slug);
    if (authorityLb) graph.push({ ...authorityLb, ...lbExtras, ...specialtyExtras });
    else {
      graph.push({
        '@type': 'LocalBusiness',
        '@id': `${canonical}#localbusiness`,
        name: 'WebCodeCraft',
        url: canonical,
        parentOrganization: { '@id': ORGANIZATION_SCHEMA_ID },
        address: {
          '@type': 'PostalAddress',
          addressLocality: area.name,
          addressRegion: area.county,
          addressCountry: 'GB'
        },
        areaServed: [area.name, area.county],
        ...lbExtras,
        ...specialtyExtras
      });
    }
    let aggregateRatingNodeCount = 0;
    for (let i = 0; i < graph.length; i += 1) {
      const node = graph[i];
      if (node && Object.prototype.hasOwnProperty.call(node, 'aggregateRating') && node.aggregateRating) {
        aggregateRatingNodeCount += 1;
      }
    }
    if (aggregateRatingNodeCount !== 1) {
      throw new Error(`local-area-jsonld: expected one aggregateRating node, got ${aggregateRatingNodeCount}`);
    }
    return JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': graph
    });
  }, [area, canonical, deep]);

  useEffect(() => {
    if (!area) return undefined;

    const title = localAreaClickyTitle(area);
    const description = localAreaCtaMetaDescription(area);
    document.title = title;
    const meta = getMetaDescriptionEl();
    const metaTitle = document.querySelector('meta[name="title"]');
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDesc = document.querySelector('meta[property="og:description"]');
    const ogUrl = document.querySelector('meta[property="og:url"]');
    const twTitle = document.querySelector('meta[property="twitter:title"]');
    const twDesc = document.querySelector('meta[property="twitter:description"]');
    const twUrl = document.querySelector('meta[property="twitter:url"]');
    const prevDesc = meta?.getAttribute('content') || '';
    const prevMetaTitle = metaTitle?.getAttribute('content') ?? '';
    const prevOgTitle = ogTitle?.getAttribute('content') ?? '';
    const prevOgDesc = ogDesc?.getAttribute('content') ?? '';
    const prevOgUrl = ogUrl?.getAttribute('content') ?? '';
    const prevTwTitle = twTitle?.getAttribute('content') ?? '';
    const prevTwDesc = twDesc?.getAttribute('content') ?? '';
    const prevTwUrl = twUrl?.getAttribute('content') ?? '';
    meta?.setAttribute('content', description);
    metaTitle?.setAttribute('content', title);
    ogTitle?.setAttribute('content', title);
    ogDesc?.setAttribute('content', description);
    ogUrl?.setAttribute('content', canonical);
    twTitle?.setAttribute('content', title);
    twDesc?.setAttribute('content', description);
    twUrl?.setAttribute('content', canonical);
    setCanonical(canonical);

    const existing = document.getElementById(schemaId);
    if (!existing && jsonLd) {
      const el = document.createElement('script');
      el.id = schemaId;
      el.type = 'application/ld+json';
      el.textContent = jsonLd;
      document.head.appendChild(el);
    }

    return () => {
      document.title = DEFAULT_PAGE_TITLE;
      meta?.setAttribute('content', prevDesc || DEFAULT_META_DESCRIPTION);
      metaTitle?.setAttribute('content', prevMetaTitle);
      ogTitle?.setAttribute('content', prevOgTitle);
      ogDesc?.setAttribute('content', prevOgDesc);
      ogUrl?.setAttribute('content', prevOgUrl);
      twTitle?.setAttribute('content', prevTwTitle);
      twDesc?.setAttribute('content', prevTwDesc);
      twUrl?.setAttribute('content', prevTwUrl);
      setCanonical(`${SITE_ORIGIN}/`);
      const script = document.getElementById(schemaId);
      if (script) script.remove();
    };
  }, [area, canonical, jsonLd, schemaId]);

  if (!area) {
    return <NotFoundPage />;
  }

  return (
    <>
      <Header />
      <main
        id="main-content"
        className="pt-24 pb-16 min-w-0 overflow-x-clip bg-gradient-to-b from-gray-50 to-white min-h-screen"
      >
        <article className="container mx-auto px-4 max-w-3xl">
          <m.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
          >
            <p className="text-sm text-indigo-600 font-medium mb-4">
              <Link to="/" className="hover:underline">
                WebCodeCraft
              </Link>
              <span className="mx-2 text-gray-400" aria-hidden="true">
                /
              </span>
              <Link to="/locations" className="hover:underline">
                Areas we serve
              </Link>
              <span className="mx-2 text-gray-400" aria-hidden="true">
                /
              </span>
              <span className="text-gray-600">{area.name}</span>
            </p>

            {isTopLandingTownSlug(area.slug) ? (
              <div className="mb-6 flex flex-col sm:flex-row flex-wrap gap-3">
                <a
                  href={internalUrl('contact')}
                  className="inline-flex w-full sm:w-auto min-h-[48px] flex-1 sm:flex-initial items-center justify-center px-6 py-3.5 rounded-xl bg-indigo-600 text-white font-semibold shadow-md hover:bg-indigo-700 transition-colors"
                >
                  Request a quote
                </a>
                <Link
                  to="/services"
                  className="inline-flex w-full sm:w-auto min-h-[48px] flex-1 sm:flex-initial items-center justify-center px-6 py-3.5 rounded-xl border-2 border-indigo-600 text-indigo-700 bg-white font-semibold shadow-sm hover:bg-indigo-50 transition-colors text-center"
                >
                  See pricing
                </Link>
              </div>
            ) : (
              <div className="mb-6 lg:hidden">
                <a
                  href={internalUrl('contact')}
                  className="inline-flex w-full sm:w-auto min-h-[48px] items-center justify-center px-6 py-3.5 rounded-xl bg-indigo-600 text-white font-semibold shadow-md hover:bg-indigo-700 transition-colors"
                >
                  Request a quote
                </a>
              </div>
            )}

            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 leading-tight">
              {deep ? deep.h1 : (
                <>
                  Web design &amp; development in {area.name}, {area.county}
                </>
              )}
            </h1>
            <p className="text-lg font-medium text-indigo-800 mb-6 leading-snug">
              {deep ? deep.lede : (
                <>
                  £0 upfront, low monthly build, hosting &amp; maintenance—bespoke sites with custom code, not WordPress
                  templates, for businesses in {area.name} and across {area.county}.
                </>
              )}
            </p>

            {area.landscape ? (
              <p
                className="text-gray-600 text-base leading-relaxed mb-6 border-l-4 border-sky-200 pl-4"
                role="note"
              >
                {area.landscape}
              </p>
            ) : null}

            <FreeSeoAuditCta className="mb-8" />

            <div className="space-y-5 text-gray-700 leading-relaxed">
              {deep ? (
                deep.paragraphs.map((p, i) => <p key={i}>{p}</p>)
              ) : (
                <>
                  <p>{leadParagraph(area)}</p>
                  <p>{secondaryParagraph(area)}</p>
                </>
              )}
              {area.slug === 'plymouth' && (
                <p>
                  Serving businesses across Plymouth and the wider{' '}
                  <Link to="/locations/exeter" className="text-indigo-600 font-medium hover:underline">
                    Exeter
                  </Link>{' '}
                  region, plus the{' '}
                  <Link to="/locations/torbay" className="text-indigo-600 font-medium hover:underline">
                    Torbay
                  </Link>{' '}
                  bayside economy (Torquay, Paignton, Brixham), we keep messaging, speed, and trust blocks aligned so your site reads as one story whether visitors find you from Ocean City or neighbouring Devon hubs.
                </p>
              )}
              {area.slug === 'truro' && (
                <p>
                  Many Truro briefs sit alongside creative and maritime firms up the Fal—see our{' '}
                  <Link to="/locations/falmouth" className="text-indigo-600 font-medium hover:underline">
                    Falmouth web design
                  </Link>{' '}
                  page for how we tune coastal Cornwall searches, portfolios, and enquiry journeys on the same monthly-first model.
                </p>
              )}
            </div>

            <LocalAreaGrowthSections slug={area.slug} areaName={area.name} />

            <h2 className="text-xl font-bold text-gray-900 mt-10 mb-3">What is included on published tiers</h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>SSL certificate (HTTPS) on plans we list publicly</li>
              <li>UK-facing hosting options and backup-minded operations</li>
              <li>Technical SEO baseline: sensible structure, performance awareness, and mobile-friendly layout</li>
              <li>Monthly hosting and care instead of a separate large upfront build invoice</li>
            </ul>

            <p className="mt-8 text-gray-700 leading-relaxed">
              Compare tiers on our{' '}
              <a href={internalUrl('services')} className="text-indigo-600 font-medium hover:underline">
                services &amp; pricing
              </a>{' '}
              page, read the{' '}
              <a href={internalUrl('faq')} className="text-indigo-600 font-medium hover:underline">
                FAQ
              </a>
              , or view{' '}
              <a href={internalUrl('portfolio')} className="text-indigo-600 font-medium hover:underline">
                recent work
              </a>
              .
            </p>

            <LocalAreaTailSection slug={area.slug} />

            <LocalAreaSpecialisms />

            {popularSouthWest.length > 0 && (
              <section className="mt-12" aria-labelledby="popular-sw-heading">
                <h2 id="popular-sw-heading" className="text-xl font-bold text-gray-900 mb-4">
                  Popular services in the South West
                </h2>
                <p className="text-gray-600 mb-5 text-sm">
                  Areas we often work alongside {area.name}—strong for regional visibility and internal discovery.
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {popularSouthWest.map((loc) => (
                    <li key={loc.slug}>
                      <Link
                        to={`/locations/${loc.slug}`}
                        className="block rounded-xl border border-gray-200 bg-white px-4 py-3 text-indigo-600 font-medium hover:border-indigo-200 hover:bg-indigo-50/40 transition-colors text-center sm:text-left"
                      >
                        {loc.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {NEARBY_LOCATIONS[area.slug] && (
              <section className="mt-12">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  Nearby locations in {area.county}
                </h2>
                <p className="text-gray-600 mb-5 text-sm">
                  We also work with businesses across these {area.county} towns.
                </p>
                <ul className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2">
                  {NEARBY_LOCATIONS[area.slug].map((loc) => (
                    <li key={loc.slug}>
                      <Link
                        to={`/locations/${loc.slug}`}
                        className="text-indigo-600 hover:text-indigo-800 hover:underline font-medium text-sm"
                      >
                        {loc.name}
                      </Link>
                    </li>
                  ))}
                </ul>
                <p className="mt-5">
                  <Link
                    to="/locations"
                    className="text-indigo-600 font-medium hover:underline text-sm"
                  >
                    View full locations list &rarr;
                  </Link>
                </p>
              </section>
            )}

            <div className="mt-12 p-6 rounded-2xl bg-gradient-to-r from-indigo-600 to-cyan-600 text-white">
              <h3 className="text-xl font-bold mb-2">Enquire from {area.name}</h3>
              <p className="mb-4 opacity-95">
                Tell us your goals—we will reply with next steps and honest fit.
              </p>
              <a
                href={internalUrl('contact')}
                className="hidden lg:inline-flex items-center justify-center px-6 py-3 bg-white text-indigo-600 font-semibold rounded-xl hover:bg-gray-100 transition-colors"
              >
                Start a project
              </a>
            </div>

            <p className="mt-10 text-sm text-gray-500">
              <Link to="/locations" className="text-indigo-600 hover:underline">
                All areas we serve
              </Link>
            </p>
          </m.div>
        </article>
      </main>
      <Footer />
      <CookieBanner />
    </>
  );
}

export default LocalAreaPage;
